/* IC testimonials: swaps the server-rendered featured reviews for every published Judge.me review.
   Source: Judge.me's public all-reviews feed (25 per page, HTML), the one its All Reviews widget uses.
   Order: featured reviews first (in their Judge.me order), then the rest, newest first.
   If the feed fails, the featured reviews already on the page stay as they are. */
(() => {
  const FEED = 'https://api.judge.me/reviews/all_reviews_js_based';
  const MAX_PAGES = 12;
  const STAR = 'M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.8l-5.2 2.8 1-5.8L1.5 7.7l5.9-.9L10 1.5z';
  const QUOTE = 'M0 16V9.6C0 4.2 2.9 1 8.1 0l.9 2.1C6.4 3 5 4.8 4.8 7.2H9V16H0Zm13 0V9.6C13 4.2 15.9 1 21.1 0l.9 2.1C19.4 3 18 4.8 17.8 7.2H22V16h-9Z';
  const TICK = '<svg class="ict__tick" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><circle cx="6" cy="6" r="6" fill="#1FA971"/><path d="M3.4 6.2l1.8 1.7 3.4-3.6" fill="none" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const esc = s => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  // Same result as Liquid's truncate: cut to n characters including the "..."
  const truncate = (s, n) => s.length > n ? s.slice(0, Math.max(0, n - 3)) + '...' : s;

  async function loadAll(shop) {
    const pages = [];
    for (let p = 1; p <= MAX_PAGES; p++) {
      const r = await fetch(`${FEED}?shop_domain=${encodeURIComponent(shop)}&platform=shopify&page=${p}&per_page=25`);
      if (!r.ok) throw new Error('feed ' + r.status);
      const j = await r.json();
      if (!j.html || !/jdgm-rev[\s'"]/.test(j.html)) break;
      pages.push(j.html);
    }
    const doc = new DOMParser().parseFromString(pages.join(''), 'text/html');
    const seen = new Set();
    return [...doc.querySelectorAll('.jdgm-rev[data-review-id]')].filter(el => {
      const id = el.getAttribute('data-review-id');
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    }).map(el => {
      const txt = sel => (el.querySelector(sel)?.textContent || '').replace(/\s+/g, ' ').trim();
      const link = el.querySelector('.jdgm-rev__prod-link');
      return {
        id: el.getAttribute('data-review-id'),
        verified: el.getAttribute('data-verified-buyer') === 'true',
        stars: parseInt(el.querySelector('.jdgm-rev__rating')?.getAttribute('data-score') || '0', 10),
        title: txt('.jdgm-rev__title'),
        body: txt('.jdgm-rev__body'),
        name: txt('.jdgm-rev__author'),
        product: link ? link.textContent.trim() : '',
        handle: link ? (link.getAttribute('href') || '').split('?')[0].split('/').pop() : ''
      };
    });
  }

  function card(r, cfg) {
    // First name + last initial for privacy: Igor Samovol -> Igor S.
    const parts = (r.name || 'Anonymous').split(/\s+/).filter(Boolean);
    const name = parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.` : (parts[0] || 'Anonymous');
    const img = cfg.images[r.handle];
    let stars = '';
    for (let i = 1; i <= 5; i++) stars += `<svg${i > r.stars ? ' class="is-off"' : ''} width="13" height="13" viewBox="0 0 20 20" aria-hidden="true"><path d="${STAR}" fill="currentColor"/></svg>`;
    const meta = [r.verified ? `${TICK}<span>Verified buyer</span>` : '', r.product ? `<span>${esc(r.product)}</span>` : '']
      .filter(Boolean).join('<span class="ict__dot" aria-hidden="true">·</span>');
    return `<li class="ict__card" data-review-id="${esc(r.id)}">
      <div class="ict__top"><svg class="ict__quote" width="22" height="16" viewBox="0 0 22 16" aria-hidden="true"><path d="${QUOTE}" fill="currentColor"/></svg>
      <span class="ict__stars" role="img" aria-label="${r.stars} out of 5 stars">${stars}</span></div>
      <blockquote class="ict__text">${r.title && cfg.titles ? `<strong>${esc(r.title)}</strong> ` : ''}${esc(truncate(r.body, cfg.chars))}</blockquote>
      <div class="ict__who">${img ? `<img class="ict__avatar" src="${esc(img)}" alt="" width="40" height="40" loading="lazy">` : ''}
      <span><span class="ict__name">${esc(name)}</span><span class="ict__meta">${meta}</span></span></div></li>`;
  }

  async function init(root) {
    if (root.dataset.ictReady) return;
    root.dataset.ictReady = '1';
    const track = root.querySelector('[data-ict-track]');
    if (!track) return;
    let images = {};
    try { images = JSON.parse(root.querySelector('[data-ict-products]')?.textContent || '{}'); } catch (e) {}
    const cfg = {
      shop: root.dataset.shop,
      limit: parseInt(root.dataset.limit || '0', 10),
      min: parseInt(root.dataset.min || '1', 10),
      chars: parseInt(root.dataset.chars || '220', 10),
      titles: root.dataset.titles === 'true',
      seconds: parseInt(root.dataset.seconds || '6', 10),
      hide: (root.dataset.hide || '').split(',').map(w => w.trim().toLowerCase()).filter(Boolean),
      images
    };
    // All featured ids in Judge.me order (the page only renders the first few of them)
    const featured = (root.dataset.featured || '').split(',').filter(Boolean);

    // Wait until the rest of the page has loaded and the browser is idle, so reviews never compete with it
    await new Promise(res => (document.readyState === 'complete' ? res() : addEventListener('load', res, { once: true })));
    await new Promise(res => ('requestIdleCallback' in window ? requestIdleCallback(res, { timeout: 3000 }) : setTimeout(res, 1200)));

    let reviews;
    try { reviews = await loadAll(cfg.shop); } catch (e) { return; }
    reviews = reviews.filter(r => {
      if (r.stars < cfg.min || r.body.length <= 20) return false;
      const hay = (r.title + ' ' + r.body).toLowerCase();
      return !cfg.hide.some(w => hay.includes(w));
    });
    const rank = id => { const i = featured.indexOf(id); return i === -1 ? featured.length : i; };
    reviews = reviews.map((r, i) => ({ r, i })).sort((a, b) => rank(a.r.id) - rank(b.r.id) || a.i - b.i).map(x => x.r);
    if (cfg.limit > 0) reviews = reviews.slice(0, cfg.limit);
    if (!reviews.length) return;

    const html = reviews.map(r => card(r, cfg)).join('');
    const swap = () => {
      track.innerHTML = html + `<li class="ict__dup" aria-hidden="true"><ul role="list">${html}</ul></li>`;
      root.style.setProperty('--ict-speed', reviews.length * cfg.seconds + 's');
      root.querySelector('.ict__viewport')?.removeAttribute('hidden');
    };
    // Swap without a visible jump: straight away if the strip is off screen, otherwise at the moment the
    // loop restarts (the strip is back at its start and the first cards are the same featured reviews).
    const viewport = root.querySelector('.ict__viewport');
    const onScreen = () => { const r = (viewport || root).getBoundingClientRect(); return r.bottom > 0 && r.top < innerHeight; };
    if (!onScreen() || matchMedia('(prefers-reduced-motion: reduce)').matches) { swap(); return; }
    let done = false;
    const once = () => { if (done) return; done = true; io && io.disconnect(); swap(); };
    track.addEventListener('animationiteration', once, { once: true });
    const io = 'IntersectionObserver' in window ? new IntersectionObserver(([e]) => { if (!e.isIntersecting) once(); }) : null;
    io && io.observe(viewport || root);
  }

  const boot = () => document.querySelectorAll('[data-ict]').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot) : boot();
  document.addEventListener('shopify:section:load', boot);
})();
